import React from 'react';

/**
 * Badge moderno para estados y etiquetas
 * @param {node} children - Contenido del badge
 * @param {string} variant - success | warning | danger | info | default | primary
 * @param {string} size - sm | md | lg
 * @param {boolean} dot - Muestra un punto indicador
 * @param {boolean} pulse - Animación de pulso
 * @param {string} className - Clases adicionales
 */
const Badge = ({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  pulse = false,
  className = '',
}) => {
  // Estilos base
  const baseStyles = `
    inline-flex items-center gap-1.5
    font-medium rounded-full
    transition-all duration-200
  `;

  // Variantes de color
  const variants = {
    primary: 'bg-sky-100 text-sky-700 border border-sky-200',
    success: 'bg-green-100 text-green-700 border border-green-200',
    warning: 'bg-amber-100 text-amber-700 border border-amber-200',
    danger: 'bg-red-100 text-red-700 border border-red-200',
    info: 'bg-blue-100 text-blue-700 border border-blue-200',
    default: 'bg-gray-100 text-gray-700 border border-gray-200',
  };

  // Colores para el dot
  const dotColors = {
    primary: 'bg-sky-500',
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    danger: 'bg-red-500',
    info: 'bg-blue-500',
    default: 'bg-gray-500',
  };

  // Tamaños
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  };

  return (
    <span
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${sizes[size]}
        ${className}
      `}
    >
      {dot && (
        <span
          className={`
            w-2 h-2 rounded-full
            ${dotColors[variant]}
            ${pulse ? 'animate-pulse' : ''}
          `}
        />
      )}
      {children}
    </span>
  );
};

// Badge con número (para notificaciones)
export const NumberBadge = ({ count, max = 99, className = '' }) => {
  const displayCount = count > max ? `${max}+` : count;

  if (count === 0) return null;

  return (
    <span
      className={`
        absolute -top-1 -right-1
        inline-flex items-center justify-center
        min-w-[1.25rem] h-5 px-1
        text-xs font-bold
        text-white
        bg-gradient-to-r from-red-500 to-orange-500
        rounded-full
        shadow-lg
        animate-pulse
        ${className}
      `}
    >
      {displayCount}
    </span>
  );
};

export default Badge;
